import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import extend from 'lodash/extend';
import { citations } from '@Constants';
import store from '@Store';
import UserDatabases from '@Services/UserDatabases';
import Breadcrumb from '@Services/Breadcrumb';
import Catalyst from '@Services/Catalyst';
import SearchStorage from '@Services/SearchStorage';
import PageData from '@Services/PageData';
import CatalystHelper from '@Services/CatalystHelper';
import BatchExecutionDetails from '@Services/BatchExecutionDetails';
import NavigationHistory from '@Services/NavigationHistory';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import { formatCitationType } from '@Components/Results/analytics/fullTextAnalytics';
import { absoluteIndexToString } from '@Containers/PatentFamilyClustering/utils';

const QUICK_SEARCH_STATE = 'main.search.quick.query';
const ADVANCED_SEARCH_STATE = 'main.search.advancedsearch';
const RESULTS_STATE = 'main.result.context';

function _initAdobeTargetView() {
  const triggerView = get(window, 'adobe.target.triggerView');
  if (triggerView) {
    triggerView(PageData.getCurrentPageName());
  }
}

export function _getLimitOrExcludeInfo({ params, totalResults }) {
  const { context, limitOrExclude } = params;

  return limitOrExclude
    ? {
        criteria: `${totalResults}`,
        dataFormCriteria: `${totalResults}`,
        type: `${Catalyst.getPrefix()}:results:${limitOrExclude}filter:${context}`,
      }
    : null;
}

export function _getFacetsSearchType({ params, facets = [] }) {
  const { context } = params;
  const facetsWithFilterUserValue = facets.filter(({ isFilterUserValue }) => isFilterUserValue);

  return !isEmpty(facetsWithFilterUserValue)
    ? {
        type:
          facetsWithFilterUserValue.length === facets.length
            ? `${Catalyst.getPrefix()}:explicit:resultsfilterbyvalue:${context}`
            : `${Catalyst.getPrefix()}:explicit:combinedfilter:${context}`,
      }
    : null;
}

export function _trackResults(trackingObject) {
  Catalyst.trackSearchResults('searchResultsUpdated', trackingObject, store.getState());
  _initAdobeTargetView();
}

async function _trackResultsFromQuickSearch(trackingObject, { params }) {
  try {
    const { searchId, description } = params;
    const storageObj = await SearchStorage.get(searchId);
    const extendedTrackingObject = {
      criteria: description,
      dataFormCriteria: storageObj.query,
    };

    _trackResults({ ...extendedTrackingObject, ...trackingObject });
  } catch (e) {
    Logger.error(e);
  }
}

function _trackResultsFromAdvancedSearch(trackingObject, { params, activeBreadcrumb = {} }) {
  const { databaseId, resultName, context } = params;
  // RXFE-5503: Result page navigation after QB searches should not generate search events
  const rootBreadcrumb = Breadcrumb.retrieveRootBreadcrumb(databaseId);

  let criteria = null;
  let dataFormCriteria = null;

  if (rootBreadcrumb.resultName === resultName) {
    criteria = CatalystHelper.getCriteriaFromAdvancedSearch(activeBreadcrumb.queryFactItem, context);
    dataFormCriteria = CatalystHelper.getDataFormCriteriaFromAdvancedSearch(activeBreadcrumb.queryFactItem, context);

    Breadcrumb.updateBreadcrumbs({
      ...rootBreadcrumb,
      criteria,
      dataFormCriteria,
    });
  } else {
    criteria = rootBreadcrumb.criteria;
    dataFormCriteria = rootBreadcrumb.dataFormCriteria;
  }

  const extendedTrackingObject = {
    criteria: criteria || CatalystHelper.getCriteriaFromAdvancedSearch(activeBreadcrumb.queryFactItem, context),
    dataFormCriteria: dataFormCriteria || CatalystHelper.getDataFormCriteriaFromAdvancedSearch(activeBreadcrumb.queryFactItem, context),
  };

  _trackResults({ ...extendedTrackingObject, ...trackingObject });
}

export const trackSearchResults = async ({ params, currentPageSize, totalResults, activeBreadcrumb }) => {
  try {
    const { pageNumber, sort, order, context, searchId, databaseId, isFromAdvancedSearch, trackedObj } = params;
    const facets = activeBreadcrumb && activeBreadcrumb.facets;
    const trackingObject = {
      database: UserDatabases.getDbName(databaseId),
      totalResults,
      resultsByType: [
        {
          name: context,
          results: totalResults,
        },
      ],
      currentPage: pageNumber,
      resultsPerPage: currentPageSize,
      sortType: `${sort}:${order}`,
      type: `${Catalyst.getPrefix()}:${isFromAdvancedSearch !== '1' ? 'explicit' : 'implicit'}:results:${context}`,
      ..._getLimitOrExcludeInfo({
        params,
        totalResults,
      }),
      ..._getFacetsSearchType({
        params,
        facets,
      }),
      ...(facets && { facets }),
      ...trackedObj,
    };

    const lastSearchState = NavigationHistory.getLastVisitedState(QUICK_SEARCH_STATE, ADVANCED_SEARCH_STATE, RESULTS_STATE);

    if (lastSearchState) {
      try {
        const batchSearchDetails = await BatchExecutionDetails.getById(searchId, databaseId);
        if (batchSearchDetails) {
          const batchCriteria = `${context}?batch file${
            !isEmpty(batchSearchDetails.items) ? `:${batchSearchDetails.items.length.toString()}` : ''
          }`;

          extend(trackingObject, {
            criteria: batchCriteria,
            dataFormCriteria: batchCriteria,
          });
        }
      } catch (e) {
        Logger.error(e);
      }

      if (lastSearchState.name === QUICK_SEARCH_STATE) {
        await _trackResultsFromQuickSearch(trackingObject, { params });
      } else if (lastSearchState.name === ADVANCED_SEARCH_STATE) {
        _trackResultsFromAdvancedSearch(trackingObject, {
          params,
          activeBreadcrumb,
        });
      }
    } else {
      // page refresh, filters, context switch, etc
      _trackResults(trackingObject);
    }
  } catch (e) {
    Logger.error(e);
  }
};

export const trackShowAllPreparations = () => {
  try {
    Catalyst.trackProductFeature(`${Catalyst.getPrefix()}:substanceresultspage:preparations`);
  } catch (e) {
    Logger.error(e);
  }
};

export const trackShowHideConditions = (show) => {
  try {
    Catalyst.trackProductFeature(`${Catalyst.getPrefix()}:reactionresults:${show ? 'showconditions' : 'hideconditions'}`);
  } catch (e) {
    Logger.error(e);
  }
};

// Checkbox tracking
const getCitationTrackingContent = (citation) => {
  const prepared = PrepareTrackingDataHelper.prepare(citation);

  return {
    id: `cit:${prepared['data-tracking-id']}`,
    authorNames: prepared['data-tracking-author-names'],
    docLink: prepared['data-tracking-doc-link'],
    title: prepared['data-tracking-title'] || 'No title',
    publicationType: formatCitationType(prepared['data-tracking-citation-type']),
    onlineDate: prepared['data-tracking-online-date'],
    publicationDate: prepared['data-tracking-publication-date'],
    indexTerms: prepared['data-tracking-index-terms'],
    issueNumber: prepared['data-tracking-issue-number'],
    volumeNumber: prepared['data-tracking-volume-number'],
  };
};

// eslint-disable-next-line import/prefer-default-export,default-param-last
export const trackResultsCheckboxChecked = (resultItem = {}, context) => {
  const { index, absoluteIndex } = resultItem;
  // Generic results
  let resultPosition = index;

  // Patent Family Clustering
  if (absoluteIndex) {
    resultPosition = absoluteIndexToString(absoluteIndex);
  }

  const trackingObj = {
    content: [
      {
        // for citations result page only
        ...(context === citations && getCitationTrackingContent(resultItem)),

        // for all checkboxes
        type: `${Catalyst.getPrefix()}:checkbox:results`,
      },
    ],
    link: {
      location: `results:${resultPosition}`, // evar118
      name: 'checkbox-on', // evar119
    },
  };

  Catalyst.trackSearchResultsClick(String(resultPosition), trackingObj);
};
